import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import isoWeeksInYear from 'dayjs/plugin/isoWeeksInYear';
import type {Availability, AvailabilityArticle} from '../interfaces/availability.type';
import type {BaseOrderLine, OrderLine, OrderLineForAvailabilityQuery} from '../interfaces/order.line.type';
import {toYearWeekString} from './date.service';

dayjs.extend(isoWeeksInYear);
dayjs.extend(isoWeek);

const AVAILABILITY_WEEKS_IN_THE_PAST = 1;
const AVAILABILITY_WEEKS_IN_THE_FUTURE = 2;

/**
 * Convert a given order line object to an order line object specific for the query to the backend.
 *
 * @param orderLine
 */
const orderLineToQuery = (orderLine: OrderLine): OrderLineForAvailabilityQuery => {
	const {articleId, formId, locationId, week, year, amount} = orderLine;

	return {
		articleId,
		formId,
		locationId,
		amount,
		weeks: getWeeksInRange(week, year)
	};
};

/**
 * Gets the weeks that are in range of the current week and year.
 * i.e: if week 3, 2021 is inputted it will return the surrounding weeks based on the lower and upper bound
 * Like week 2, 2021, week 4 and week 5 of 2021 if the input was 3, 2021, -1, 2
 *
 * @param week
 * @param year
 */
export const getWeeksInRange = (week: number, year: number) => {
	if (dayjs().year(year).isoWeeksInYear() < week) {
		throw new Error('Week is not in range of the total weeks for current year.');
	}

	const weeksArr = [];

	// Add previous weeks.
	for (let i = AVAILABILITY_WEEKS_IN_THE_PAST; i > 0; i--) {
		const previousWeek = week - i;

		if (previousWeek > 0) {
			weeksArr.push(toYearWeekString(year, previousWeek));
		} else {
			const lastYear = year - 1;
			const lastWeekOfLastYear = dayjs().year(lastYear).isoWeeksInYear();
			weeksArr.push(toYearWeekString(lastYear, lastWeekOfLastYear - Math.abs(previousWeek)));
		}
	}

	// Add current week
	weeksArr.push(toYearWeekString(year, week));

	// Add next weeks.
	const weeksInCurrentYear = dayjs().year(year).isoWeeksInYear();

	for (let i = 1; i <= AVAILABILITY_WEEKS_IN_THE_FUTURE; i++) {
		const nextWeek = week + i;

		if (nextWeek <= weeksInCurrentYear) {
			weeksArr.push(toYearWeekString(year, nextWeek));
		} else {
			const nextYear = year + 1;
			const nextWeekOfNextYear = nextWeek - weeksInCurrentYear;
			weeksArr.push(toYearWeekString(nextYear, nextWeekOfNextYear));
		}
	}

	return weeksArr;
};

/**
 * Builds the query that is used by the api request to get the availabilities for the surrounding weeks.
 *
 * @param orderLines
 */
export const orderLinesToAvailabilityQuery = (orderLines: OrderLine[]): OrderLineForAvailabilityQuery[] => {
	return orderLines.map((orderLine: OrderLine) => orderLineToQuery(orderLine));
};

/**
 * If no availability returns from the backend for this specific week we return a custom
 * availability object where availability is 0. Since we don't get an availability from PAT
 * we set no availability just to be sure.
 *
 * We know our weekString is always in the format of toYearAndWeekStr (i.e. 202101, 202152 etc.)
 * so we can sub-string the value to get the first 4 characters for the year and the last 2 characters for the week
 *
 * @param weekString
 */
export const emptyAvailabilityForWeek = (weekString: string): Availability => {
	const week = parseInt(weekString.substring(4, 6));
	const year = parseInt(weekString.substring(0, 4));

	if (isNaN(week) || isNaN(year)) {
		throw new Error('Week or year is not a number');
	}

	if (dayjs().isoWeeksInYear() < week) {
		throw new Error('Week is not in range of the total weeks for current year');
	}

	return {year, week, amount: 0};
};

/**
 * Sort the availabilities on the correct week numbers
 *
 * @param x1
 * @param x2
 */
export const sortAvailabilities = (x1: Availability, x2: Availability) => parseInt(toYearWeekString(x1.year, x1.week)) - parseInt(toYearWeekString(x2.year, x2.week));

/**
 * Matches the availability article against the order line.
 *
 * @param article
 * @param orderLine
 */
export const matchesAvailabilityAgainstOrderLine = (article: AvailabilityArticle, orderLine: BaseOrderLine | OrderLine) => {
	return article.articleId === orderLine.articleId
		&& article.formId === orderLine.formId
		&& (article.locationId === null || article.locationId === orderLine.locationId);
}

/**
 * Get the availability for a specific order line.
 *
 * @param orderLine
 * @param availabilities
 */
export const getAvailabilitiesByVariety = (orderLine: BaseOrderLine, availabilities: AvailabilityArticle[]): AvailabilityArticle | undefined => {
	if (!orderLine || !availabilities) {
		return undefined;
	}

	return availabilities.find((article: AvailabilityArticle) => matchesAvailabilityAgainstOrderLine(article, orderLine));
};

/**
 * Get the availability for a specific order line.
 *
 * @param orderLine
 * @param availabilities
 */
export const getAvailabilityByOrderLine = (orderLine: BaseOrderLine, availabilities: AvailabilityArticle[]): Availability | undefined => {
	const article = getAvailabilitiesByVariety(orderLine, availabilities);

	if (!article) {
		return {
			week: orderLine.week,
			year: orderLine.year,
			amount: 0
		};
	}

	return article.availability.find((a: Availability) => a.week === orderLine.week && a.year === orderLine.year);
};
